import React, { Component } from 'react'
import { withRouter } from 'react-router'
import { Link } from 'react-router-dom'
import { inject, observer } from 'mobx-react'
import { extendObservable, toJS, computed } from 'mobx'
import propTypes from 'prop-types'
import DocumentTitle from 'react-document-title'
import get from 'lodash/get'
import find from 'lodash/find'

import { formatDate, formatNumber } from '@atheneum/api/dist/lib'
import { eplApi } from '@atheneum/api'

import InvoicesAndCreditNotes from './InvoicesAndCreditNotes'
import ACL from '@components/ACL'
import Action from '@components/Action'
import Attachment from '@components/Attachment'
import { showDialog, isCancellationError } from '@components/Dialog'
import ArrowBackIcon from '@components/Icon/ArrowBack'
import DeleteIcon from '@components/Icon/Delete'
import EditIcon from '@components/Icon/Edit'
import Spinner from '@components/spinner'

class Fee extends Component {
  static propTypes = {
    match: propTypes.object.isRequired,
    feeStore: propTypes.object.isRequired,
    projectStore: propTypes.object.isRequired,
    staticData: propTypes.object.isRequired,
    history: propTypes.object.isRequired,
    growlStore: propTypes.object.isRequired,
    ruleStore: propTypes.object.isRequired
  }

  constructor(props) {
    super(props)
    this.state = {
      projectedMargin: 'calculating...',
      isCalculatingMargin: true
    }
    const { feeStore } = props

    extendObservable(this, {
      attachments: computed(() => {
        if (feeStore.feeRaw) {
          return feeStore.feeRaw.attachments
        }
      }),
      fee: null,
      feeItem: computed(() => {
        if (feeStore.feeRaw) {
          return find(feeStore.feeRaw.feeItems, i => i.feeTypeId)
        }
      }),
      costItem: computed(() => {
        if (feeStore.feeRaw) {
          return find(feeStore.feeRaw.feeItems, i => i.costTypeId)
        }
      }),
      projectId: this.props.projectStore.project.id
    })

    feeStore.get(this.props.match.params.feeId)
  }

  generateBackLink = () => {
    const { match } = this.props
    return match.url.substr(0, match.url.lastIndexOf('/fee')) + '/outcome'
  }

  getTitle(isTextForDeleteModal) {
    const { feeStore, staticData } = this.props
    if (!(feeStore.feeRaw && (this.costItem || this.feeItem))) return

    const expert = get(feeStore.feeRaw, 'expert')
    let type

    if (this.costItem && !this.feeItem) {
      type = staticData.names.costTypes[this.costItem.costTypeId]
    } else {
      type = staticData.names.feeTypes[this.feeItem.feeTypeId]
    }
    return isTextForDeleteModal ? (
      `${type}${expert ? ` - ${expert.title} ${expert.fullName}` : ''}`
    ) : (
      <span>
        {type}
        {expert && (
          <span>
            &nbsp;-&nbsp;{' '}
            <Link to={'/expert/' + expert.id}>
              {expert.title} {expert.fullName}
            </Link>
          </span>
        )}
      </span>
    )
  }

  renderFeeDetails() {
    const { feeStore, staticData } = this.props
    const { currencies } = staticData.names

    if (!this.feeItem || !feeStore.feeRaw) return null

    return (
      <div>
        <h2>Fee details</h2>
        <div className='details-list'>
          <div>Fee type:</div>
          <div>{staticData.names.feeTypes[this.feeItem.feeTypeId]}</div>
        </div>
        <div className='details-list'>
          <div>Date:</div>
          <div>{formatDate(feeStore.feeRaw.deliveryDate)}</div>
        </div>
        <div className='details-list'>
          <div>Fee:</div>
          <div>
            {formatNumber(this.feeItem.value || 0)}{' '}
            {staticData.names.currencies[this.feeItem.currencyId]}
          </div>
        </div>
        <div className='details-list'>
          <div>Fee amount in EUR:</div>
          <div>{formatNumber(this.feeItem.converted)}</div>
        </div>

        <div className='details-list'>
          <div>
            {currencies[this.feeItem.currencyId]}
            /EUR conversion rate:
          </div>
          <div>{formatNumber(this.feeItem.conversionRate, 6)}</div>
        </div>

        <div className='details-list'>
          <div>Credits:</div>
          <div>{formatNumber(this.feeItem.credits)}</div>
        </div>
        <div className='details-list'>
          <div>Contract:</div>
          <div>
            {feeStore.feeRaw.contract
              ? feeStore.feeRaw.contract.contractReference
              : '-'}
          </div>
        </div>
        <div className='details-list'>
          <div>Note:</div>
          <div>{this.feeItem.note || '-'}</div>
        </div>
      </div>
    )
  }

  renderCostDetails() {
    const { feeStore, staticData } = this.props
    if (!feeStore.feeRaw || !this.costItem) return
    return (
      <div>
        <h2>Cost Details</h2>
        <div className='details-list'>
          <div>Cost type:</div>
          <div>{staticData.names.costTypes[this.costItem.costTypeId]}</div>
        </div>
        {this.costItem.entityName && (
          <div className='details-list'>
            <div>Entity name:</div>
            <div>{this.costItem.entityName}</div>
          </div>
        )}
        {this.costItem.invoiceNumber && (
          <div className='details-list'>
            <div>Invoice number:</div>
            <div>{this.costItem.invoiceNumber}</div>
          </div>
        )}
        {feeStore.feeRaw.expert && (
          <div className='details-list'>
            <div>Expert:</div>
            <div>
              <Link to={'/expert/' + feeStore.feeRaw.expert.id}>
                {feeStore.feeRaw.expert.title} {feeStore.feeRaw.expert.fullName}
              </Link>
            </div>
          </div>
        )}
        <div className='details-list'>
          <div>Cost item value:</div>
          <div>
            {formatNumber(this.costItem.value || 0)}{' '}
            {staticData.names.currencies[this.costItem.currencyId]}
          </div>
        </div>
        <div className='details-list'>
          <div>Note:</div>
          <div>{this.costItem.note || '-'}</div>
        </div>
      </div>
    )
  }

  async calculateMarginPercentage(feeItem, costItem) {
    const callInfo = {
      fee: feeItem.value,
      feeCurrencyId: feeItem.currencyId,
      honorarium: costItem.value,
      honorariumCurrencyId: costItem.currencyId
    }
    const margin = await eplApi.recalculateMargin(callInfo)
    this.setState({
      projectedMargin: (margin.projectedMargin * 100).toFixed(2) + '%',
      isCalculatingMargin: false
    })
  }

  componentDidUpdate(props) {
    if (this.state.isCalculatingMargin) {
      this.calculateMarginPercentage(this.feeItem, this.costItem)
    }
  }

  renderReportingDetails() {
    const { feeStore } = this.props
    if (!this.feeItem || !feeStore.feeRaw) return null
    return (
      <div>
        <h2>Reporting Details</h2>
        {this.costItem ? (
          <div className='details-list'>
            <div>Margin Percentage:</div>
            <div>
              {this.state.projectedMargin}
              {/* {this.calculateMarginPercentage(this.feeItem, this.costItem)} */}
            </div>
          </div>
        ) : null}
      </div>
    )
  }

  handleFeeEdit = () => {
    const { projectStore, feeStore } = this.props
    if (!feeStore.feeRaw) return
    return this.props.feeStore.open(feeStore.feeRaw.id, projectStore.project.id)
  }

  handleFeeDelete = feeId => {
    const { feeStore, projectStore, history, growlStore } = this.props
    return showDialog({
      title: 'Delete this fee?',
      text: this.getTitle(true),
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete'
    })
      .then(() => {
        return feeStore.delete(feeId).then(res => {
          history.push(`/project-search/project/${projectStore.id}/outcome`)
        })
      })
      .catch(err => {
        if (isCancellationError(err)) return
        growlStore.showError(err)
      })
  }

  getDocumentTitle(fee) {
    if (!fee || !fee.expert) return 'Consultation outcome | Atheneum Partners'
    return `${fee.expert.title} ${fee.expert.fullName
      } consultation outcome | Atheneum Partners`
  }

  renderEditAction = () => {
    const { feeStore, ruleStore } = this.props
    const { feeRaw } = feeStore

    const action = (
      <Action alignRight>
        <li data-cy='edit-fee-btn' onClick={this.handleFeeEdit}>
          <EditIcon />
          <span>Edit</span>
        </li>
      </Action>
    )

    if (ruleStore.isDeliveryDateAffectedByBackdating(feeRaw.deliveryDate)) {
      return (
        <ACL roles={['Accounting', 'Admin']} action>
          {action}
        </ACL>
      )
    }

    return action
  }

  renderDeleteAction = () => {
    const { feeStore, ruleStore } = this.props
    const { feeRaw } = feeStore

    if (ruleStore.isDeliveryDateAffectedByBackdating(feeRaw.deliveryDate)) {
      return null
    }

    return (
      <Action alignRight>
        <li onClick={() => this.handleFeeDelete(feeRaw.id)}>
          <DeleteIcon />
          <span>Delete</span>
        </li>
      </Action>
    )
  }

  render() {
    let { feeStore, match } = this.props
    if (!feeStore.feeRaw) return <Spinner />

    const feeId = parseInt(match.params.feeId)

    return (
      <DocumentTitle title={this.getDocumentTitle(feeStore.feeRaw)}>
        <div className='invoice-wrapper animate-intro'>
          <div className='actions-wrapper actions-wrapper__noTop actions-wrapper--space-between clearfix'>
            <ul className='actions right-list'>
              {this.renderEditAction()}
              {this.renderDeleteAction()}
            </ul>
            <ul className='actions left-list'>
              <Link to={this.generateBackLink()}>
                <Action>
                  <li>
                    <ArrowBackIcon />
                    <span>Project outcome</span>
                  </li>
                </Action>
              </Link>
            </ul>
          </div>
          <div>
            <h2>{this.getTitle()}</h2>
            <div className='details-list'>
              <div>Deliverable:</div>
              <div>{get(feeStore.feeRaw, 'deliverable.name')}</div>
            </div>
            {this.renderFeeDetails()}
            {this.renderCostDetails()}
            {this.renderReportingDetails()}
            <InvoicesAndCreditNotes feeId={feeId} />
            <div className='m-t-lg'>
              <Attachment
                attachments={toJS(this.attachments)}
                readOnly
                label='Attachments'
              />
            </div>
          </div>
        </div>
      </DocumentTitle>
    )
  }
}

export default withRouter(
  inject('feeStore', 'projectStore', 'staticData', 'growlStore', 'ruleStore')(
    observer(Fee)
  )
)
